/**
 * 贡献视图的**纯展示逻辑**（唯一一份 · 贡献卡与它的 spec 共用）
 *
 * 依据：L 域 PRD **§5.3-6 / §5.3-7**（贡献视图口径 · 窗口与单人态）· **§5.3-8**（红线）·
 * **§7.3**（`AC-L3.6`–`L3.10`）· `DR-037`（**两桶** · `D1`/`D2`/`D5`）·
 * 契约 `contract/contribution.ts`。
 *
 * ## 🔴 边界：本文件**一个金额都不算**
 *
 * | 判定 | 谁定 |
 * |---|---|
 * | 两种桶各是多少支出 | 🔴 **服务端**（C 域 `composeExpense` 唯一内核，`AC-L3.6`）—— 端侧**绝不**求第二次和 |
 * | 窗口起止日（`from` / `to`） | 🔴 **服务端**（账本时区 + `nucleus/calendar.ts`）—— 端侧不算日期 |
 * | 单人账本判定 | 🔴 **服务端** `singleMember`（`ledger_members` 行数）—— 端侧**不数** `buckets.length` |
 * | 谁是我 | 🔴 **服务端** `isSelf` —— 端侧**不比对** `userId`（同 `LedgerMemberView.isSelf`） |
 * | 「我 / Ta」怎么称呼、有没有该显示的空态 | 本文件（**纯文案**，无判定语义） |
 *
 * ## 🔴 为什么"我"取 `buckets` 的第 0 项是**安全的**，而"数桶"是不安全的
 *
 * 服务端保证**排序**：调用者本人恒在首位（`ContributionBucketView` 契约明写）。
 * 所以"我 = `[0]`"是**契约保证**，不是巧合推测。
 * 但"人数 = `buckets.length`"**不能**推导出"是不是单人账本" —— 成员上限 4、
 * 且**没有支出的成员也会补 0 进桶**，所以两者在数值上碰巧相等，语义上无关。
 *
 * ## 🔴 红线（`AC-L3.9` · `§5.3-8`）—— 本文件的每条文案都受它约束
 *
 * **不排名 · 不评分 · 不做成员对比评价 · 不做"谁花得多"榜单 · 不做进度竞赛**。
 * ⇒ 本文件**不产出**「占比 %」「第 N 名」「领先 / 落后」「超过 Ta」「平均线」这类字段，
 * 也**不产出**任何可以拿来比较的评价词（"多 / 少 / 高 / 低"一律不出现）。
 * 排序只为了让"我先看到自己"，**不是名次**。
 */

import {
  ContributionWindow,
  LedgerRole,
  type ContributionBucketView,
  type ContributionView,
} from '@jiaying/shared';

/** 一个成员在贡献卡里的**展示行**（= 服务端桶 + 「我 / Ta」称呼） */
export interface ContributionRow {
  userId: string;
  /** 服务端补的"我在首位"排序里的这一位 */
  isSelf: boolean;
  /** 展示名（服务端下发） */
  userName: string;
  /** 「我」或成员名 —— 端侧只做称呼，不改身份 */
  displayName: string;
  /** 角色中文名（"所有者" / "成员"） */
  roleLabel: string;
  /** 记录支出（服务端算好的整数分 + 币种）—— 🔴 端侧**不重算** */
  expense: number;
  /** 名下账户数（服务端算好的**口径已排除 `internal` 与隐藏**） */
  accountCount: number;
  /** 「记录支出」这一栏的说明 —— 把"按记录人不按归属人"讲清楚（`AC-L3.14` 的 UI 侧） */
  expenseHint: string;
  /** 「管理 N 个账户」这一栏的说明 —— 与上一栏**维度不同**，必须分开标注（`DR-037` D5） */
  accountHint: string;
}

/** 贡献卡的**展示就绪**形态 */
export interface ContributionCardModel {
  /** 窗口中文名（`thisMonth` → 「本月」） */
  windowLabel: string;
  /** 窗口说明（含服务端给的两个日期，端侧只拼字符串） */
  rangeText: string;
  /** 🔴 单人账本 ⇒ 整块隐藏，改显示邀请引导卡（`AC-L3.10` / `AC-L7.2`） */
  hidden: boolean;
  /** 展示行（顺序 = 服务端顺序：我恒在首位） */
  rows: readonly ContributionRow[];
  /**
   * 总支出展示文本所需的原始值（整数分）。
   *
   * 🔴 端侧**不求和** —— 这里直接取服务端 `totalExpense`。
   * `totalExpense` 与各行之和相等是**服务端的结构性保证**（`DR-037` D2），
   * 端侧做"校验式求和"就是第二份求和实现。
   */
  totalExpense: number;
  /** 🔴 多币种诚实标注 —— 非 `null` 时 UI **必须**把这句显示出来（`DR-004` 禁跨币种相加） */
  currencyNotice: string | null;
}

/** 窗口 → 中文名（**唯一一份**；新窗口值漏了这里是编译错误） */
export const CONTRIBUTION_WINDOW_LABEL: Record<ContributionWindow, string> = {
  [ContributionWindow.ThisMonth]: '本月',
  [ContributionWindow.Last3Months]: '近 3 月',
  [ContributionWindow.ThisYear]: '今年',
};

/** 窗口下拉 / 分段控件的选项（顺序 = 契约 `CONTRIBUTION_WINDOWS`，从窄到宽） */
export const CONTRIBUTION_WINDOW_OPTIONS: readonly { value: ContributionWindow; label: string }[] = [
  ContributionWindow.ThisMonth,
  ContributionWindow.Last3Months,
  ContributionWindow.ThisYear,
].map((w) => ({ value: w, label: CONTRIBUTION_WINDOW_LABEL[w] }));

/** 窗口中文名；未知取值**回退原值**（不猜、不空着 —— 同 `policyStatusLabel`） */
export function contributionWindowLabel(window: string): string {
  return CONTRIBUTION_WINDOW_LABEL[window as ContributionWindow] ?? window;
}

/** 角色中文名（与 `SettingsPage` 成员卡的措辞**同一套**：所有者 / 成员） */
export function contributionRoleLabel(role: string): string {
  return role === LedgerRole.Owner ? '所有者' : '成员';
}

/**
 * 称呼：**我** 或 成员名。
 *
 * ⚠️ 用服务端 `isSelf`，**不比对** `userId` —— 端侧自己没有"当前用户 id"这个权威值
 * （token 解出来的 id 与账本上下文无关，比对会引入第二个判据）。
 */
export function contributionDisplayName(bucket: ContributionBucketView): string {
  return bucket.isSelf ? '我' : bucket.userName;
}

/**
 * 窗口区间文案 —— 只拼服务端下发的两个日期，**不算日期**。
 *
 * ⚠️ 不写"截至今天"这类需要端侧知道"今天"的措辞：`to` 是**账本时区的今天**，
 * 端侧本地日期在跨时区时可能与它不同（`DR-004` 同一类问题）。
 */
export function contributionRangeText(from: string, to: string): string {
  return `${from} ~ ${to}`;
}

/**
 * 把服务端视图转成**展示就绪**的卡片模型。
 *
 * 🔴 单人账本时仍返回模型（`hidden = true` + 单行），但 UI 据此**整块换掉**为邀请引导卡 ——
 * 不在本函数里决定"显示哪张卡"（那是组件的分支，纯函数只给判据）。
 */
export function toContributionCard(view: ContributionView): ContributionCardModel {
  return {
    windowLabel: contributionWindowLabel(view.window),
    rangeText: contributionRangeText(view.from, view.to),
    hidden: view.singleMember,
    rows: view.buckets.map((b) => ({
      userId: b.userId,
      isSelf: b.isSelf,
      userName: b.userName,
      displayName: contributionDisplayName(b),
      roleLabel: contributionRoleLabel(b.role),
      expense: b.expense,
      accountCount: b.accountCount,
      // 🔴 两栏的说明必须**分开写** —— 「谁记的」与「钱是谁的」是两个维度（`DR-037` D5）
      expenseHint: '按记录人统计',
      accountHint: '按归属人统计 · 不含挂账账户',
    })),
    totalExpense: view.totalExpense,
    currencyNotice: view.currencyMixed
      ? '本账本含多币种账户 —— 此处仅统计本币账户，不做跨币种折算'
      : null,
  };
}
